import Button from '@navi/web-ui/lib/primitives/Button';
import Tag from '@navi/web-ui/lib/primitives/Tag';
import { ICellRendererParams } from 'ag-grid-community/dist/lib/rendering/cellRenderers/iCellRenderer';
import { GLOBAL } from 'src/constants/Global';
import { FeedbackTypesCodeMap } from 'src/pages/Cases/constants/CasesConstants';
import { setFieldVisitModalId, User } from 'src/reducers/commonSlice';
import store from 'src/store';
import { dateFormat, utcToIST } from 'src/utils/DateHelper';
import AudioControls from '@cp/components/Waveform/AudioControls';
import { DateFormat, getFormatDate } from '../../../utils/DateHelper';
import FeedbackActionButton from '../feedbackForm/component/FeedbackActionButton';
import styles from '../components/CaseDetail.module.scss';
import { feedbackHavingInteractionStatus } from './utils';
import FieldFeedbackAction from '../feedbackForm/component/FieldFeedbackAction';
import { AnswerType, AnswerView, QuestionTags } from '../interfaces/CommunicationHistory.type';
import { Tooltip, TooltipContent, TooltipTrigger } from '@cp/src/components/TooltipV2/TooltipV2';
import EllipsisText from '@cp/src/components/EllipsisText';
import DateTime from '../components/DateTime';
import { Typography } from '@navi/web-ui/lib/primitives';
import {
  SKIP_TRACING_TYPES,
  SKIP_TRACING_TYPES_LABEL
} from '@cp/pages/CaseDetails/feedbackForm/constants';
import { NaviTableColumnDef } from '@cp/src/components/NaviTable/interfaces';
import cx from 'classnames';
import { CallType } from '@cp/src/components/Waveform/const';

export const CALL_INTERACTIONS = 'callInteractions';
export const FIELD_CALL_INTERACTIONS = 'fieldCallInteractions';
export const NO_RECORD_FOUND = 'No Record Found';

export enum TRANSACTION_STATUS_TYPE {
  FAILED = 'FAILED',
  PAID = 'PAID'
}

export enum TRANSACTION_STATUS_MAP {
  FAILED = 'Failed',
  SUCCESS = 'Success',
  SCHEDULED = 'Scheduled'
}

export enum CALL_INTERACTIONS_TYPE {
  CALL_BRIDGE = 'CALL_BRIDGE',
  SELF_CALL = 'SELF_CALL',
  AMEYO = 'AMEYO',
  HUMAN_REMINDER = 'HUMAN_REMINDER',
  WHATSAPP_CALL = 'WHATSAPP_CALL',
  WHATSAPP_TEXT = 'WHATSAPP_TEXT',
  SKIP_TRACING_TELE = 'SKIP_TRACING_TELE',
  INBOUND_CALL = 'INBOUND_CALL',
  BOT_REMINDER = 'BOT_REMINDER'
}

export const SYSTEM_INTERACTIONS_TYPE = {
  WHATSAPP: 'WHATSAPP',
  SMS: 'SMS',
  IVR: 'VOICE',
  EMAIL: 'EMAIL',
  PUSH_NOTIFICATION: 'PUSH_NOTIFICATION',
  FIELD_VISIT: 'FIELD_VISIT',
  FIELD_CALLING: 'FIELD_CALLING'
};

export const COMMUNICATION_HISTORY_TAB_KEYS: any = {
  [SYSTEM_INTERACTIONS_TYPE.WHATSAPP]: 'whatsapp',
  [SYSTEM_INTERACTIONS_TYPE.SMS]: 'sms',
  [SYSTEM_INTERACTIONS_TYPE.IVR]: 'ivr',
  [SYSTEM_INTERACTIONS_TYPE.EMAIL]: 'email',
  [SYSTEM_INTERACTIONS_TYPE.PUSH_NOTIFICATION]: 'pushNotification',
  [SYSTEM_INTERACTIONS_TYPE.FIELD_VISIT]: 'fieldFeedback',
  [SYSTEM_INTERACTIONS_TYPE.FIELD_CALLING]: 'fieldCalling'
};

export type TCOMMUNICATION_HISTORY_TAB_KEYS = keyof typeof COMMUNICATION_HISTORY_TAB_KEYS;
export type TCOMMUNICATION_HISTORY_TAB_VALUES =
  (typeof COMMUNICATION_HISTORY_TAB_KEYS)[TCOMMUNICATION_HISTORY_TAB_KEYS];

export const QuestionTextTypes = {
  CUSTOMER_INTERACTION_STATUS: 'Customer Interaction Status'
};

export const SYSTEM_INTERACTIONS_TYPE_COL_DEFS = [
  {
    field: 'scheduledTimestamp',
    headerName: 'Date & Time',
    width: '150px',
    cellStyle: { cursor: 'auto', paddingRight: '0' },
    cellRenderer: (params: ICellRendererParams) => {
      const istDate = new Date(params.value);
      return <DateTime istDate={istDate} />;
    }
  },
  {
    field: 'phoneNumber',
    headerName: 'Contacted On',
    width: '160px',
    cellStyle: { paddingRight: '0', cursor: 'auto' },
    cellRenderer: (params: ICellRendererParams) => {
      const phoneNumber = params.data?.phoneNumber;
      return <div className="rr-block">{phoneNumber}</div>;
    }
  },
  {
    field: 'message',
    headerName: 'Message',
    cellStyle: {
      wordBreak: 'break-word',
      paddingRight: '0',
      paddingTop: '8',
      paddingBottom: '8',
      cursor: 'auto'
    },
    width: '470px',
    autoHeight: true,
    wrapText: true
  },
  {
    field: 'status',
    headerName: 'Status',
    cellStyle: { cursor: 'auto' }
  }
];

export const SYSTEM_INTERACTIONS_FIELD_VISIT_TYPE_COL_DEFS = [
  {
    field: 'createdAt',
    headerName: 'Date & Time',
    flex: 3,
    width: '230px',
    cellStyle: { cursor: 'auto' },
    cellRenderer: (params: ICellRendererParams) => {
      const value = dateFormat(utcToIST(params.value), DateFormat.LONG_DATE_FORMAT_WITH_TIME);

      return (
        <Tooltip placement="top" hiddenPadding={0} hideStrategy="referenceHidden">
          <TooltipTrigger tooltipTriggerClassName="tooltipTriggerWrapper">
            <EllipsisText text={value} />
          </TooltipTrigger>
          <TooltipContent className="tooltipWrapper">{value}</TooltipContent>
        </Tooltip>
      );
    }
  },
  {
    field: 'agencyName',
    headerName: 'Agent Contact',
    flex: 1,
    width: '150px',
    cellStyle: { cursor: 'auto' },
    cellRenderer: (params: ICellRendererParams) => (
      <div>
        <div className={cx(styles.agentContact, 'rr-block')}>
          {params?.data?.agentPhoneNumber || '--'}
        </div>
        <div className={styles.agencyName}>{params?.data?.metadata?.agencyName || '--'}</div>
      </div>
    )
  },
  {
    field: 'status',
    headerName: 'Feedback',
    flex: 3,
    cellStyle: { cursor: 'auto' },
    cellRendererSelector: (params: ICellRendererParams) => {
      const feedbackType = params.data?.metadata?.tag;
      const { color = 'blue', label = feedbackType || 'Not Available' } =
        FeedbackTypesCodeMap[feedbackType] || {};
      if (!label) return null;
      return {
        component: Tag,
        params: {
          color,
          label,
          variant: 'transparent'
        }
      };
    }
  },
  {
    field: 'imageUrls',
    headerName: 'Image',
    flex: 1,
    width: '100px',
    cellRendererSelector: (params: ICellRendererParams) => {
      const imageUploaded = params.data?.answerViews?.filter(
        (ansViews: AnswerView) => ansViews?.questionTag === QuestionTags.IMAGE_UPLOAD
      );
      return {
        component: Button,
        params: {
          variant: 'text',
          children: 'View',
          disabled: !imageUploaded?.length,
          onClick: () => {
            store.dispatch(setFieldVisitModalId(params?.data?.id));
          }
        }
      };
    }
  },
  {
    field: 'details',
    headerName: 'Details',
    width: '100px',
    cellRendererSelector: (params: ICellRendererParams) => {
      const lat = params.data?.metadata?.interactionLatitude;
      const long = params.data?.metadata?.interactionLongitude;
      const url = `http://maps.google.com/?q=${lat},${long}`;
      const filteredAnswerViews = params.data?.answerViews?.filter(
        (answer: AnswerView) => answer?.questionTag !== QuestionTags.IMAGE_UPLOAD
      );
      return {
        component: FieldFeedbackAction,
        params: {
          answerViews: filteredAnswerViews,
          comments: params.data?.comments,
          gMapLocationUrl: url
        }
      };
    }
  }
];

export enum InteractionStatuses {
  CUSTOMER_INTERACTION_STATUS = 'INTERACTION_STATUS',
  HUMAN_REMINDER_FEEDBACK_FORM = 'INTERACTION_STATUS',
  TERMINAL_INTERACTION_STATUS = 'TERMINAL_INTERACTION_STATUS'
}

export const callTypesMap = new Map([
  ['CALL_BRIDGE', 'System'],
  ['AMEYO', 'Ameyo'],
  ['SELF_CALL', 'Self'],
  ['HUMAN_REMINDER', 'Reminder'],
  ['DEFAULT', '--'],
  ['SKIP_TRACING', 'System'],
  ['WHATSAPP_CALL', 'Whatsapp Call'],
  ['WHATSAPP_TEXT', 'Whatsapp Text'],
  ['SKIP_TRACING_TELE', 'Customer Outreach'],
  ['GEN_AI_BOT', 'Reminder Chat'],
  ['INBOUND_CALL', 'Inbound Call'],
  ['SKIP_TRACING_CALL', 'Customer Outreach call'],
  ['SKIP_TRACING_TEXT', 'Customer Outreach text'],
  ['SKIP_TRACING_OTHERS', 'Customer Outreach others'],
  ['BOT_REMINDER', 'Automated Reminder'],
  ['VENDOR_POC', 'Vendor POC']
]);

export const airtelCallTypesMap = new Map([
  ['OUTBOUND', 'Outbound'],
  ['INBOUND', 'Inbound'],
  ['NA', '---']
]);

const columnWidth = (percentage: number) => {
  const parent = document.getElementById('callInteractionTable') as HTMLElement;
  if (parent && parent?.offsetWidth) {
    return (parent.offsetWidth * percentage) / 100;
  }
  return 200;
};

const attributesWidth = {
  dateWidth: (percentage: number) =>
    GLOBAL.SCREEN_WIDTH < 1400 ? '150px' : `${columnWidth(percentage)}px`,
  recipientNumberWidth: (percentage: number) =>
    GLOBAL.SCREEN_WIDTH < 1400 ? '130px' : `${columnWidth(percentage)}px`,
  feedBackWidth: (percentage: number) =>
    GLOBAL.SCREEN_WIDTH < 1400 ? '213px' : `${columnWidth(percentage)}px`,
  typeWidth: (percentage: number) =>
    GLOBAL.SCREEN_WIDTH < 1400 ? '90px' : `${columnWidth(percentage)}px`,
  audioButtonWidth: (percentage: number) =>
    GLOBAL.SCREEN_WIDTH < 1400 ? '162px' : `${columnWidth(percentage)}px`,
  actionWidth: (percentage: number) =>
    GLOBAL.SCREEN_WIDTH < 1400 ? '90px' : `${columnWidth(percentage)}px`
};

export const interactionFeedbackStyles = (interactionLevel: number) => {
  if (interactionLevel === 1) {
    return styles.red;
  }
  if (interactionLevel === 2) {
    return styles.orange;
  }
  if (interactionLevel === 3) {
    return styles.green;
  }
  return styles.blue;
};

export const getFieldCallsInteractionsColDefs = (): NaviTableColumnDef<any, any>[] => [
  {
    accessorKey: 'createdAt',
    header: 'Date & Time',
    cell: info => {
      const interaction = info?.row?.original;
      const createdAt = interaction?.createdAt;
      const istDate = utcToIST(createdAt);
      return <DateTime istDate={istDate} />;
    }
  },
  {
    accessorKey: 'phoneNumber',
    header: 'Phone',
    cell: info => {
      const interaction = info?.row?.original;
      const contactNumber = interaction?.customerPhoneNumber;
      return (
        <Typography className="rr-block" variant={'p3'} color={'var(--grayscale-2)'}>
          {contactNumber || '-'}
        </Typography>
      );
    }
  },
  {
    accessorKey: 'type',
    header: 'Type',
    cell: info => {
      const interaction = info?.row?.original;
      const vendorType = interaction?.providerType;
      return vendorType || '-';
    }
  },
  {
    accessorKey: 'audioUrl',
    header: 'Recording',
    size: 200,
    cell: info => {
      const interaction = info?.row?.original;
      const audioUrl: string = interaction?.recordingData?.url;
      if (!audioUrl) return '-';
      return (
        <div className="w-[180px]">
          <AudioControls
            audioUrl={audioUrl}
            metaData={{
              interactionId: interaction?.callId,
              interactionCreatedAt: interaction?.createdAt,
              phoneNumber: interaction?.customerPhoneNumber,
              callType: interaction?.providerType
            }}
          />
        </div>
      );
    }
  }
];

export const getInteractionsFeedbackColumns = (user: User) => {
  return [
    {
      field: 'createdAt',
      headerName: 'Date & Time',
      width: attributesWidth.dateWidth(20),
      cellStyle: (params: ICellRendererParams) => {
        return {
          paddingLeft: '12px',
          paddingRight: '12px',
          lineHeight: '1.5rem',
          cursor: params.data?.ameyoNonConnectedInteractionsInfo?.length > 1 ? 'pointer' : 'auto'
        };
      },
      cellRenderer: (params: ICellRendererParams) => {
        const isSelfCallAndStartTimeExists =
          params.data.type === CallType.SELF_CALL && params.data.metadata?.START_TIMESTAMP_MS;
        const istDate = isSelfCallAndStartTimeExists
          ? new Date(Number(params.data.metadata?.START_TIMESTAMP_MS))
          : utcToIST(
              params.data.ameyoNonConnectedInteractionsInfo?.length
                ? params.data.ameyoNonConnectedInteractionsInfo[0].createdAt
                : params.value
            );
        return <DateTime istDate={istDate} />;
      }
    },
    {
      field: 'recipientNumber',
      headerName: 'Phone',
      width: attributesWidth.recipientNumberWidth(14),
      cellStyle: (params: ICellRendererParams) => ({
        paddingLeft: '6px',
        paddingRight: '6px',
        lineHeight: '1.5rem',
        cursor: params.data.ameyoNonConnectedInteractionsInfo?.length > 1 ? 'pointer' : 'auto'
      }),
      cellRenderer: (params: ICellRendererParams) => {
        const phoneNumber = params.data?.source?.recipientNumber;
        return <div className="rr-block">{phoneNumber}</div>;
      }
    },
    {
      field: 'answerViews',
      headerName: 'Feedback',
      width: attributesWidth.feedBackWidth(23),
      cellStyle: (params: ICellRendererParams) => ({
        paddingLeft: '6px',
        paddingRight: '6px',
        lineHeight: '1.5rem',
        cursor: params.data.ameyoNonConnectedInteractionsInfo?.length > 1 ? 'pointer' : 'auto'
      }),
      cellRenderer: (params: ICellRendererParams) => {
        const interactionFeedback = feedbackHavingInteractionStatus(params);
        return interactionFeedback?.optionText ? (
          <div className={styles.feedbackWrapper}>
            <div className={interactionFeedbackStyles(interactionFeedback?.interactionLevel)}>
              {interactionFeedback?.optionText}
            </div>
            <div className={styles.dispositionHierarchyTag}>
              {params.data?.answerHierarchy?.hierarchyTag}
            </div>
          </div>
        ) : params.data?.ameyoNonConnectedInteractionsInfo?.length ? (
          <div className={styles.feedbackWrapper}>
            <div
              className={interactionFeedbackStyles(
                params.data?.ameyoNonConnectedInteractionsInfo[0]?.interactionLevel
              )}
            >
              {params.data?.ameyoNonConnectedInteractionsInfo[0]?.callStatusView}
            </div>
            <div className={styles.dispositionHierarchyTag}>
              {params.data?.answerHierarchy?.hierarchyTag}
            </div>
          </div>
        ) : params.data?.type === CALL_INTERACTIONS_TYPE.INBOUND_CALL ? (
          <div className={styles.feedbackWrapper}>
            <div className={interactionFeedbackStyles(params.data?.metadata?.interactionLevel)}>
              {params.data?.metadata?.answerText}
            </div>
            <div className={styles.dispositionHierarchyTag}>
              {params.data?.answerHierarchy?.hierarchyTag}
            </div>
          </div>
        ) : (
          '-'
        );
      }
    },
    {
      field: 'type',
      headerName: 'Type',
      width: attributesWidth.typeWidth(12),
      cellStyle: (params: ICellRendererParams) => ({
        paddingLeft: '4px',
        paddingRight: '4px',
        lineHeight: '1.5rem',
        cursor: params.data.ameyoNonConnectedInteractionsInfo?.length > 1 ? 'pointer' : 'auto',
        wordBreak: 'auto-phrase'
      }),
      cellRenderer: (params: ICellRendererParams) => {
        if (
          [
            SKIP_TRACING_TYPES.SKIP_TRACING_CALL,
            SKIP_TRACING_TYPES.SKIP_TRACING_TEXT,
            SKIP_TRACING_TYPES.SKIP_TRACING_OTHERS
          ].includes(params.data?.type)
        ) {
          return (
            <div className={styles.skipTracingFeedbackRow}>
              <Typography className={styles.header} variant="p3" color="var(--navi-color-gray-c2)">
                Customer Outreach
              </Typography>
              <Typography
                className={styles.subheader}
                variant="p3"
                color="var(--grayscale-content-3)"
              >
                {SKIP_TRACING_TYPES_LABEL[params.data?.type as SKIP_TRACING_TYPES]}
              </Typography>
            </div>
          );
        }
        return callTypesMap.get(params?.data?.type) || callTypesMap.get('DEFAULT');
      }
    },
    {
      field: 'audioUrl',
      headerName: 'Recording',
      width: attributesWidth.audioButtonWidth(20),
      cellStyle: (params: ICellRendererParams) => ({
        paddingLeft: '6px',
        paddingRight: '4px',
        lineHeight: '1.5rem',
        cursor: params.data.ameyoNonConnectedInteractionsInfo?.length > 1 ? 'pointer' : 'auto'
      }),
      cellRendererSelector: (params: ICellRendererParams) => {
        const audioUrl: string = params.data?.metadata?.recordingUrl;
        return {
          component: AudioControls,
          params: {
            audioUrl,
            metaData: {
              interactionId: params.data?.id,
              interactionCreatedAt: params.data?.createdAt,
              phoneNumber: params.data?.source?.recipientNumber,
              callType: params.data?.type
            },
            type: params.data?.type,
            selfCallMetadata: params.data?.metadata
          }
        };
      }
    },
    {
      field: 'action',
      headerName: 'Action',
      width: attributesWidth.actionWidth(11),
      cellStyle: { paddingLeft: '4px', paddingRight: '0px', lineHeight: '1.5rem' },
      cellRendererSelector: (params: ICellRendererParams) => {
        return {
          component: FeedbackActionButton,
          params: {
            answerViews: params.data?.answerViews,
            referenceId: params.data?.referenceId || '',
            interactionType: params.data?.type,
            comments: params.data?.comments,
            ameyoNonConnectedInteractionsInfo: params.data?.ameyoNonConnectedInteractionsInfo,
            toggleState: params.data?.toggleState,
            interactionData: params.data
          }
        };
      }
    }
  ];
};

export const getTodayDate = () => {
  const today = new Date();
  return today;
};

export const getYesterdayDate = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return yesterday;
};

export const getLastMonthDate = () => {
  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() - 1);
  return lastMonth;
};

export const getNextThreeMonthDate = () => {
  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() + 3);
  return lastMonth;
};
